/**
 * La orden, en un diálogo.
 *
 * POR QUE NO UN PANEL AL LADO DE LA LISTA
 * ---------------------------------------
 * Era lo que habia: al tocar una fila, la pantalla se partia en dos columnas y
 * la ficha se metia en la mitad derecha. Tres cosas se rompian ahi.
 *
 * 1. El ancho. A 1280px (el monitor del local) cada columna queda en ~600px, y
 *    la ficha tiene dos columnas adentro y un encabezado con cuatro botones.
 *    Los botones no achican (eran `shrink-0`), asi que se quedaban con el ancho
 *    entero y el titulo del equipo colapsaba a dos letras: "Ip".
 * 2. La atencion. Mirar una orden y mirar la lista son dos tareas distintas.
 *    Tenerlas al lado obliga a elegir donde mirar cada vez.
 * 3. El mobile. La lista directamente desaparecia y habia que apretar "Volver
 *    a la lista", un boton que solo existia por el layout.
 *
 * Con el diálogo, los tres se van: 768px enteros para la ficha, la lista queda
 * atenuada atras (contexto, no competencia), y en el celular ocupa la pantalla
 * completa con la X de siempre. Es ademas el patron que ya usan las otras diez
 * pantallas del taller.
 *
 * ANATOMIA: IDENTIDAD ARRIBA, DATOS AL MEDIO, ACCIONES ABAJO
 * ----------------------------------------------------------
 * El encabezado dice QUE orden es y de quien es el equipo. El cuerpo (lo unico
 * que scrollea) dice como viene. El pie, fijo, tiene lo que hay que hacer: una
 * primaria a la derecha y las secundarias a la izquierda. Sacar las acciones
 * del encabezado no es solo prolijidad: es lo que hace que el titulo tenga
 * ancho propio y no se pelee con los botones.
 *
 * LA URL SIGUE MANDANDO
 * ---------------------
 * `?ot=123` abre el diálogo y cerrarlo lo saca. El buscador global enlaza ahi
 * y el link se puede pasar por WhatsApp; que sea un diálogo no le saca la
 * direccion.
 */
import { ArrowLeft, SearchX } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { formatearFecha } from "@/lib/utils";

import {
  AccionesDeLaOrden,
  ClienteDeLaOrden,
  CuerpoOrden,
  type FichaOrdenProps,
} from "./FichaOrden";
import {
  TONO_ESTADO,
  diasEnTaller,
  esInterna,
  etiquetaEstadoDe,
  nombreOrden,
  textoAntiguedad,
} from "./logica";

export interface DialogOrdenProps extends FichaOrdenProps {
  onCerrar: () => void;
}

export function DialogOrden({ onCerrar, ...ficha }: DialogOrdenProps): JSX.Element {
  const { orden } = ficha;
  const dias = diasEnTaller(orden);

  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      {/*
        `lg` (768px) es el tope de diálogo del sistema: mas que eso ya es una
        pantalla adentro de otra. Entra justo lo que la ficha necesita, las dos
        tarjetas al lado y el desglose sin apretar.

        `hoja`: en el celular ocupa la pantalla entera. Una ficha de este largo
        centrada en 375px con margenes de 16 es una ventanita con scroll adentro
        de una pagina con scroll.
      */}
      <DialogContent tamano="lg" mobile="hoja" aria-describedby={undefined}>
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={TONO_ESTADO[orden.estado]}>{etiquetaEstadoDe(orden)}</Badge>
            {/* "Stock" es una etiqueta, no un estado: por eso va en outline. */}
            {esInterna(orden) ? <Badge variant="outline">Stock</Badge> : null}
            <DialogTitle className="min-w-0 truncate text-lg tracking-tight">
              {orden.equipo}
            </DialogTitle>
          </div>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <Codigo>{nombreOrden(orden)}</Codigo>
            <span>· entró el {formatearFecha(orden.fecha_ingreso)}</span>
            <span>· {textoAntiguedad(orden, dias)}</span>
          </p>
          <ClienteDeLaOrden orden={orden} />
        </DialogHeader>

        <DialogBody>
          <CuerpoOrden {...ficha} />
        </DialogBody>

        {/* `sm:items-center` alinea el menu "Mas" (36px) con la primaria (40). */}
        {/* Abajo y no en la barra de arriba: son seis acciones o más y acá no
            se escribe, así que el teclado no las tapa. */}
        <DialogFooter className="sm:items-center" enCelular="abajo">
          <AccionesDeLaOrden {...ficha} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * `?ot=` con un numero que no esta entre las ordenes cargadas.
 *
 * Va en un diálogo y no en la pagina para que la lista siga ahi atras: el que
 * llego por un link viejo no perdio el taller, perdio una orden.
 */
export function DialogOrdenNoEncontrada({
  tope,
  onCerrar,
}: {
  tope: number;
  onCerrar: () => void;
}): JSX.Element {
  return (
    <Dialog open onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent tamano="sm" aria-describedby={undefined}>
        {/* El titulo visible lo pone el vacio; este es el que lee el lector
            de pantalla al abrirse el diálogo. */}
        <DialogTitle className="sr-only">No encontramos esa orden</DialogTitle>
        <EstadoVacio
          icono={SearchX}
          titulo="No encontramos esa orden"
          detalle={`Puede ser más vieja que las últimas ${tope} órdenes, o no existir.`}
          accion={
            <Button type="button" variant="outline" onClick={onCerrar}>
              <ArrowLeft aria-hidden="true" />
              Volver a la lista
            </Button>
          }
        />
      </DialogContent>
    </Dialog>
  );
}
